import type { DocumentDeclaration, ModuleManifest } from '@nexus/core';
import { useQuery } from '@tanstack/react-query';
import type { WebModule } from '../modules/define';
import type { DocumentProvider } from './provider';

/** A declared document with the module that owns it and its data provider, if it has one. */
export interface FoundDocument {
  owner: ModuleManifest;
  declaration: DocumentDeclaration;
  load?: () => Promise<{ default: DocumentProvider }>;
}

export function findDocument(modules: readonly WebModule[], key: string): FoundDocument | null {
  for (const module of modules) {
    const declaration = module.manifest.documents?.find((d) => d.key === key);
    if (declaration) {
      return { owner: module.manifest, declaration, load: module.documents?.[key] };
    }
  }
  return null;
}

/**
 * Every data provider of a web module belongs to a document its manifest declares. A document
 * without provider gets the kernel data only: findings, signatures, kernel placeholders.
 */
export function checkDocuments(modules: readonly WebModule[]): void {
  const problems: string[] = [];
  for (const module of modules) {
    const declared = new Set((module.manifest.documents ?? []).map((d) => d.key));
    for (const key of Object.keys(module.documents ?? {})) {
      if (!declared.has(key)) {
        problems.push(
          `${module.manifest.id}: document provider "${key}" is not declared in the manifest`,
        );
      }
    }
  }
  if (problems.length > 0) throw new Error(problems.join('\n'));
}

/** The data provider of a document, loaded on first use as a chunk of its module. */
export function useDocumentProvider(found: FoundDocument | null): {
  provider: DocumentProvider | null;
  pending: boolean;
  failed: boolean;
} {
  const load = found?.load;
  const query = useQuery({
    queryKey: ['documents', 'provider', found?.declaration.key],
    queryFn: async () => (await load!()).default,
    enabled: !!load,
    staleTime: Infinity,
    gcTime: Infinity,
    structuralSharing: false,
    // Without network the chunk comes from the precache of the service worker.
    networkMode: 'always',
  });
  if (!load) return { provider: null, pending: false, failed: false };
  return { provider: query.data ?? null, pending: query.isPending, failed: query.isError };
}
